/**
 * Console SOS alarm — pure state model (P0-14).
 *
 * One SOS can reach a console on two paths: the `personnel:sos-alert` socket
 * broadcast, and the 10 s polling fallback (GET /personnel-tracking/sos/open)
 * that exists because the socket can be down. This module decides what is on
 * screen, independent of React:
 *
 *  - IDENTITY. An SOS is identified by its emergency check-in id (`event_id`
 *    on the socket, `sos_event_id` from the poll), so the same SOS heard on
 *    both paths is one event. A polled row with no check-in is keyed by
 *    session, which stays stable while the session sits in emergency status
 *    (its updated_at moves with every position upload, so it must never be
 *    part of the key).
 *  - THE SOCKET ALONE ONLY RAISES A SERVER-RECORDED SOS. Only the REST trigger
 *    (POST /personnel-tracking/sos: permission-checked, session-owned, written
 *    to the DB, audited) stamps `event_id` on the broadcast. A socket payload
 *    without one is unverifiable, so it is never an alarm by itself: the hook
 *    treats it as a hint to re-poll, and the alarm appears only once the server
 *    lists the session. This is what stops a forged or replayed broadcast from
 *    putting a siren on every console in the tenant.
 *  - ONE ENTRY PER RESPONDER SESSION. A repeat SOS from a session whose alarm
 *    is still up folds into that entry instead of stacking a second one.
 *  - ACKNOWLEDGEMENT IS LOCAL AND EXPLICIT. Nothing but `ack` removes an
 *    entry: not a timeout, not the server dropping the session. Acknowledging
 *    records every event key the entry absorbed, so later polls of that same
 *    SOS stay quiet, while a NEW SOS (new check-in id) alarms again.
 */

export const SOS_ACK_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;
export const SOS_ACK_MAX_ENTRIES = 500;
/**
 * The modal renders at most this many responder entries; the rest are counted
 * ("N more open SOS") and move up as entries above are acknowledged. Every
 * alarm stays in state — a real SOS is never dropped — but a burst of entries
 * can no longer grow the dialog without bound.
 */
export const SOS_ALARM_RENDER_LIMIT = 20;

/** Payload of the `personnel:sos-alert` socket event (backend map.socket.ts). */
export interface SosSocketEvent {
  type?: 'personnel:sos-alert';
  session_id: string;
  user_id?: string;
  user_name?: string;
  tenantId?: string;
  lat?: number | null;
  lng?: number | null;
  message?: string;
  timestamp?: string;
  /** Emergency check-in id. Set only by the REST trigger; required to alarm from the socket. */
  event_id?: string;
  incident_id?: string;
  incident_name?: string;
  is_exercise?: boolean;
}

/** Row from GET /personnel-tracking/sos/open. */
export interface OpenSosRow {
  session_id: string;
  user_id?: string | null;
  user_name?: string | null;
  callsign?: string | null;
  role?: string | null;
  incident_id?: string | null;
  incident_name?: string | null;
  is_exercise?: boolean | null;
  sos_event_id?: string | null;
  sos_at?: string | null;
  sos_lat?: number | string | null;
  sos_lng?: number | string | null;
  last_lat?: number | string | null;
  last_lng?: number | string | null;
  last_position_at?: string | null;
}

export interface SosAlarm {
  sessionId: string;
  /** Every SOS identity folded into this entry; all are acknowledged together. */
  eventKeys: string[];
  userName: string;
  callsign: string | null;
  role: string | null;
  /** When the SOS was raised (server time, ISO 8601), if known. */
  sosAt: string | null;
  sosLat: number | null;
  sosLng: number | null;
  lastLat: number | null;
  lastLng: number | null;
  lastPositionAt: string | null;
  incidentId: string | null;
  incidentName: string | null;
  /** true = drill, false = real incident / no incident, null = not yet known. */
  isExercise: boolean | null;
  message: string | null;
  /** Console clock when this entry first appeared. */
  firstSeenAt: number;
  /** The polling endpoint has reported this session at least once. */
  seenByPoll: boolean;
  /** A later successful poll no longer lists the session as in emergency. */
  closedOnServer: boolean;
}

export interface SosAlarmState {
  /** Unacknowledged alarms, in arrival order (stable, so buttons never shift under a finger). */
  alarms: SosAlarm[];
  /** Acknowledged event keys -> console time of acknowledgement. */
  acked: Record<string, number>;
}

export type SosAlarmAction =
  | { type: 'socket'; event: SosSocketEvent; now: number }
  | { type: 'poll'; rows: OpenSosRow[]; now: number }
  | { type: 'ack'; sessionId: string; now: number }
  | { type: 'reset'; acked: Record<string, number> };

export function createInitialSosAlarmState(acked: Record<string, number> = {}): SosAlarmState {
  return { alarms: [], acked };
}

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

/** A socket SOS the server recorded: it carries the emergency check-in id. */
export type RecordedSosSocketEvent = SosSocketEvent & { event_id: string };

export function socketEventKey(event: RecordedSosSocketEvent): string {
  return `evt:${event.event_id}`;
}

export function pollRowKey(row: OpenSosRow): string {
  if (row.sos_event_id) return `evt:${row.sos_event_id}`;
  return `session:${row.session_id}`;
}

/** Minimal runtime guard: a socket payload is trusted only as far as its shape. */
export function isSosSocketEvent(value: unknown): value is SosSocketEvent {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { session_id?: unknown }).session_id === 'string' &&
    (value as { session_id: string }).session_id.length > 0
  );
}

/**
 * True only when the payload carries the server-issued SOS event id, which the
 * REST trigger path alone sets. Anything else is a hint, never an alarm.
 */
export function isRecordedSosSocketEvent(value: unknown): value is RecordedSosSocketEvent {
  if (!isSosSocketEvent(value)) return false;
  const id = (value as { event_id?: unknown }).event_id;
  return typeof id === 'string' && id.length > 0;
}

// ---------------------------------------------------------------------------
// Normalisation
// ---------------------------------------------------------------------------

/** pg DOUBLE PRECISION arrives as a number; DECIMAL would arrive as a string. Accept both. */
function num(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function validIso(value: unknown): string | null {
  const s = str(value);
  if (!s) return null;
  return Number.isFinite(new Date(s).getTime()) ? s : null;
}

export function alarmFromSocketEvent(event: RecordedSosSocketEvent, now: number): SosAlarm {
  return {
    sessionId: event.session_id,
    eventKeys: [socketEventKey(event)],
    userName: str(event.user_name) ?? 'Unknown responder',
    callsign: null,
    role: null,
    sosAt: validIso(event.timestamp),
    sosLat: num(event.lat),
    sosLng: num(event.lng),
    lastLat: null,
    lastLng: null,
    lastPositionAt: null,
    incidentId: str(event.incident_id),
    incidentName: str(event.incident_name),
    isExercise: typeof event.is_exercise === 'boolean' ? event.is_exercise : null,
    message: str(event.message),
    firstSeenAt: now,
    seenByPoll: false,
    closedOnServer: false,
  };
}

export function alarmFromOpenSosRow(row: OpenSosRow, now: number): SosAlarm {
  return {
    sessionId: row.session_id,
    eventKeys: [pollRowKey(row)],
    userName: str(row.user_name) ?? 'Unknown responder',
    callsign: str(row.callsign),
    role: str(row.role),
    sosAt: validIso(row.sos_at),
    sosLat: num(row.sos_lat),
    sosLng: num(row.sos_lng),
    lastLat: num(row.last_lat),
    lastLng: num(row.last_lng),
    lastPositionAt: validIso(row.last_position_at),
    incidentId: str(row.incident_id),
    incidentName: str(row.incident_name),
    isExercise: typeof row.is_exercise === 'boolean' ? row.is_exercise : null,
    message: null,
    firstSeenAt: now,
    seenByPoll: true,
    closedOnServer: false,
  };
}

// ---------------------------------------------------------------------------
// Merge
// ---------------------------------------------------------------------------

function timeOf(iso: string | null): number {
  return iso ? new Date(iso).getTime() : Number.NEGATIVE_INFINITY;
}

/**
 * Fold `incoming` (same session) into `existing`. The newer SOS wins for the
 * SOS time and SOS position; known values never get overwritten by unknowns;
 * poll data is authoritative for the session's last position and drill flag.
 */
function mergeAlarm(existing: SosAlarm, incoming: SosAlarm): SosAlarm {
  const incomingIsNewer = timeOf(incoming.sosAt) > timeOf(existing.sosAt);
  const sosSource = incomingIsNewer ? incoming : existing;
  const newKeys = incoming.eventKeys.filter((k) => !existing.eventKeys.includes(k));
  const eventKeys = newKeys.length === 0 ? existing.eventKeys : [...existing.eventKeys, ...newKeys];

  return {
    ...existing,
    eventKeys,
    userName:
      existing.userName === 'Unknown responder' && incoming.userName !== 'Unknown responder'
        ? incoming.userName
        : existing.userName,
    callsign: incoming.callsign ?? existing.callsign,
    role: incoming.role ?? existing.role,
    sosAt: sosSource.sosAt ?? existing.sosAt ?? incoming.sosAt,
    sosLat: sosSource.sosLat ?? existing.sosLat ?? incoming.sosLat,
    sosLng: sosSource.sosLng ?? existing.sosLng ?? incoming.sosLng,
    lastLat: incoming.lastLat ?? existing.lastLat,
    lastLng: incoming.lastLng ?? existing.lastLng,
    lastPositionAt: incoming.lastPositionAt ?? existing.lastPositionAt,
    incidentId: incoming.incidentId ?? existing.incidentId,
    incidentName: incoming.incidentName ?? existing.incidentName,
    isExercise:
      incoming.seenByPoll && incoming.isExercise !== null
        ? incoming.isExercise
        : existing.isExercise ?? incoming.isExercise,
    message: incoming.message ?? existing.message,
    seenByPoll: existing.seenByPoll || incoming.seenByPoll,
    closedOnServer: incoming.seenByPoll ? false : existing.closedOnServer,
  };
}

const COMPARED_FIELDS: (keyof SosAlarm)[] = [
  'userName', 'callsign', 'role', 'sosAt', 'sosLat', 'sosLng', 'lastLat', 'lastLng',
  'lastPositionAt', 'incidentId', 'incidentName', 'isExercise', 'message', 'seenByPoll',
  'closedOnServer',
];

function sameAlarm(a: SosAlarm, b: SosAlarm): boolean {
  if (a.eventKeys.length !== b.eventKeys.length) return false;
  return COMPARED_FIELDS.every((f) => a[f] === b[f]);
}

/** Returns the same array instance when nothing changed (avoids needless renders). */
function ingest(alarms: SosAlarm[], acked: Record<string, number>, incoming: SosAlarm): SosAlarm[] {
  if (incoming.eventKeys.some((k) => k in acked)) return alarms;

  const index = alarms.findIndex((a) => a.sessionId === incoming.sessionId);
  if (index === -1) return [...alarms, incoming];

  const merged = mergeAlarm(alarms[index], incoming);
  if (sameAlarm(merged, alarms[index])) return alarms;
  const next = alarms.slice();
  next[index] = merged;
  return next;
}

// ---------------------------------------------------------------------------
// Acknowledgement bookkeeping
// ---------------------------------------------------------------------------

/** Drop acknowledgements older than the retention window and cap the map size. */
export function pruneAcks(acked: Record<string, number>, now: number): Record<string, number> {
  const entries = Object.entries(acked)
    .filter(([, at]) => typeof at === 'number' && Number.isFinite(at) && now - at <= SOS_ACK_RETENTION_MS)
    .sort((a, b) => b[1] - a[1])
    .slice(0, SOS_ACK_MAX_ENTRIES);
  return Object.fromEntries(entries);
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export function sosAlarmReducer(state: SosAlarmState, action: SosAlarmAction): SosAlarmState {
  switch (action.type) {
    case 'socket': {
      // No server-issued event id: not an alarm on its own (see the header).
      if (!isRecordedSosSocketEvent(action.event)) return state;
      const alarms = ingest(state.alarms, state.acked, alarmFromSocketEvent(action.event, action.now));
      return alarms === state.alarms ? state : { ...state, alarms };
    }

    case 'poll': {
      let alarms = state.alarms;
      const polledSessions = new Set<string>();
      for (const row of action.rows) {
        if (!row || typeof row.session_id !== 'string' || !row.session_id) continue;
        polledSessions.add(row.session_id);
        alarms = ingest(alarms, state.acked, alarmFromOpenSosRow(row, action.now));
      }

      // The server no longer lists a session it previously reported: say so on
      // the alarm, but KEEP the alarm — only a human clears it.
      let changed = alarms !== state.alarms;
      const reconciled = alarms.map((a) => {
        if (a.seenByPoll && !a.closedOnServer && !polledSessions.has(a.sessionId)) {
          changed = true;
          return { ...a, closedOnServer: true };
        }
        return a;
      });
      return changed ? { ...state, alarms: reconciled } : state;
    }

    case 'ack': {
      const target = state.alarms.find((a) => a.sessionId === action.sessionId);
      if (!target) return state;
      const acked = { ...state.acked };
      for (const key of target.eventKeys) acked[key] = action.now;
      return {
        alarms: state.alarms.filter((a) => a.sessionId !== action.sessionId),
        acked: pruneAcks(acked, action.now),
      };
    }

    case 'reset':
      return createInitialSosAlarmState(action.acked);

    default:
      return state;
  }
}
